/**
 * FileDropzone Component
 *
 * Reusable drag-and-drop file upload component using react-dropzone.
 * Supports file type filtering, size limits, and visual feedback.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { Box, Typography, Paper } from "@mui/material";
import { CloudUpload } from "@mui/icons-material";

interface FileDropzoneProps {
  onFileSelect: (file: File) => void;
  onFilesSelect?: (files: File[]) => void;
  acceptedFormats?: Record<string, string[]>;
  maxSizeMB?: number;
  multiple?: boolean;
  maxFiles?: number;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFileSelect,
  onFilesSelect,
  acceptedFormats = { "application/pdf": [".pdf"] },
  maxSizeMB = 10,
  multiple = false,
  maxFiles,
}) => {
  const { t } = useTranslation(["common"]);
  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept: acceptedFormats,
      maxSize: maxSizeMB * 1024 * 1024,
      multiple,
      maxFiles,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
          if (multiple && onFilesSelect) {
            onFilesSelect(acceptedFiles);
          } else {
            onFileSelect(acceptedFiles[0]);
          }
        }
      },
    });

  const dropText = multiple
    ? isDragActive
      ? t("common:fileUpload.dropFilesHere")
      : t("common:fileUpload.dragDropFiles")
    : isDragActive
      ? t("common:fileUpload.dropFileHere")
      : t("common:fileUpload.dragDropFile");

  return (
    <Box>
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.400",
          bgcolor: isDragActive ? "action.hover" : "background.paper",
          cursor: "pointer",
          textAlign: "center",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: "action.hover",
          },
        }}
        elevation={0}
        data-testid="file-upload"
      >
        <input {...getInputProps()} />
        <CloudUpload
          sx={{
            fontSize: 64,
            color: isDragActive ? "primary.main" : "grey.500",
            mb: 2,
            transition: "color 0.2s",
          }}
        />
        <Typography variant="h6" gutterBottom>
          {dropText}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {t("common:fileUpload.orClickToBrowse")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block" }}
        >
          {t("common:fileUpload.acceptedFormats", {
            formats: Object.values(acceptedFormats).flat().join(", "),
            maxSize: maxSizeMB,
            maxFiles: maxFiles
              ? `, ${t("common:fileUpload.upToFiles", { count: maxFiles })}`
              : "",
          })}
        </Typography>
      </Paper>

      {fileRejections.length > 0 && (
        <Typography color="error" sx={{ mt: 2 }} variant="body2">
          {fileRejections[0].errors[0].message}
        </Typography>
      )}
    </Box>
  );
};

export default FileDropzone;
